import React from 'react';
import { ArrowUpRight, type LucideIcon } from 'lucide-react';

export type KpiAccent = 'green' | 'blue' | 'purple' | 'orange';

export interface KpiMetricLine {
  value: string;
  label?: string;
}

const ACCENT_STYLES: Record<KpiAccent, { border: string; icon: string }> = {
  green: {
    border: 'border-l-[var(--primary)]',
    icon: 'bg-[#86BE41]/10 text-[var(--primary)]',
  },
  blue: {
    border: 'border-l-[var(--mymoolah-blue)]',
    icon: 'bg-[#2D8CCA]/10 text-[var(--mymoolah-blue)]',
  },
  purple: {
    border: 'border-l-[#6b4fa8]',
    icon: 'bg-[#6b4fa8]/10 text-[#6b4fa8]',
  },
  orange: {
    border: 'border-l-[#e67e22]',
    icon: 'bg-[#e67e22]/10 text-[#e67e22]',
  },
};

export interface KpiMetricCardProps {
  label: string;
  metrics: KpiMetricLine[];
  footnote?: string;
  icon: LucideIcon;
  accent: KpiAccent;
  onClick: () => void;
}

export const KpiMetricCard: React.FC<KpiMetricCardProps> = ({
  label,
  metrics,
  footnote,
  icon: Icon,
  accent,
  onClick,
}) => {
  const accentStyle = ACCENT_STYLES[accent];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`portal-kpi-card group ${accentStyle.border}`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <span className="portal-kpi-card__label">{label}</span>
        <div className="flex shrink-0 items-center gap-1">
          <div className={`portal-kpi-card__icon-wrap ${accentStyle.icon}`}>
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <ArrowUpRight
            className="h-4 w-4 text-[var(--muted-foreground)] opacity-0 transition group-hover:opacity-100"
            aria-hidden
          />
        </div>
      </div>

      <div className="portal-kpi-metrics">
        {metrics.map((metric) => (
          <div
            key={`${metric.value}-${metric.label ?? 'solo'}`}
            className="portal-kpi-metric-row"
          >
            <span className="portal-kpi-value">{metric.value}</span>
            {metric.label ? (
              <span className="portal-kpi-metric-label">{metric.label}</span>
            ) : null}
          </div>
        ))}
      </div>

      {footnote ? <p className="portal-kpi-footnote">{footnote}</p> : null}
    </button>
  );
};
